import React from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Chip,
  Alert
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import KioskReceiptSettingsModal, { KioskReceiptDisplaySettings } from './KioskReceiptSettingsModal';
import { useTranslation } from 'react-i18next';

interface KioskReceiptSettingsTabProps {
  receiptSettings: KioskReceiptDisplaySettings;
  onReceiptSettingsChange: (settings: KioskReceiptDisplaySettings) => void;
  onImageUpload?: (file: File) => Promise<string>;
}

const KioskReceiptSettingsTab: React.FC<KioskReceiptSettingsTabProps> = ({
  receiptSettings,
  onReceiptSettingsChange,
  onImageUpload
}) => {
  const { t } = useTranslation('salesSystem');
  const [receiptSettingsModalOpen, setReceiptSettingsModalOpen] = React.useState(false);

  console.log('🔍 [KIOSK RECEIPT TAB] Props:', {
    hasReceiptSettings: !!receiptSettings,
    hasOnReceiptSettingsChange: !!onReceiptSettingsChange,
    hasOnImageUpload: !!onImageUpload
  });

  const handleOpenReceiptSettingsModal = () => {
    console.log('📋 [KIOSK RECEIPT TAB] Adisyon ayarları modalı açılıyor');
    setReceiptSettingsModalOpen(true);
  };

  const handleCloseReceiptSettingsModal = () => {
    console.log('📋 [KIOSK RECEIPT TAB] Adisyon ayarları modalı kapatılıyor');
    setReceiptSettingsModalOpen(false);
  };

  const handleSaveReceiptSettings = (newSettings: KioskReceiptDisplaySettings) => {
    console.log('💾 [KIOSK RECEIPT TAB] Adisyon ayarları kaydediliyor:', newSettings);
    onReceiptSettingsChange(newSettings);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <ReceiptIcon />
        {t('kioskReceiptTab.title')}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        {t('kioskReceiptTab.description')}
      </Alert>

      {/* Adisyon Ayarları Özet Kartı */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            {t('kioskReceiptTab.currentSettings')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={handleOpenReceiptSettingsModal}
            size="large"
          >
            {t('kioskReceiptTab.actions.editSettings')}
          </Button>
        </Box>

        <Grid container spacing={2}>
          {/* Başlık Bilgisi */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('kioskReceiptTab.summary.headerType')}
              </Typography>
              <Chip 
                label={receiptSettings.headerType === 'text' ? t('kioskReceiptTab.summary.textHeader') : t('kioskReceiptTab.summary.logoHeader')}
                color={receiptSettings.headerType === 'text' ? 'primary' : 'secondary'}
                size="small"
              />
            </Box>
          </Grid>

          {/* Şirket Adı */}
          {receiptSettings.headerType === 'text' && (
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('kioskReceiptTab.summary.companyName')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {receiptSettings.companyName || '-'}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Logo URL */}
          {receiptSettings.headerType === 'logo' && (
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('kioskReceiptTab.summary.logo')}
                </Typography>
                {receiptSettings.logoUrl ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img 
                      src={receiptSettings.logoUrl} 
                      alt={t('kioskReceiptTab.summary.logoAlt')} 
                      style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4 }}
                    />
                    <Chip label={t('kioskReceiptTab.summary.logoUploaded')} color="success" size="small" />
                  </Box>
                ) : (
                  <Chip label={t('kioskReceiptTab.summary.logoNotUploaded')} color="warning" size="small" />
                )}
              </Box>
            </Grid>
          )}

          {/* Yazdırma Kopya Sayısı */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('kioskReceiptTab.summary.copyCount')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {t('kioskReceiptTab.summary.copyCountValue', { count: receiptSettings.printCopyCount || 1 })}
              </Typography>
            </Box>
          </Grid>

          {/* KDV Gösterimi */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('kioskReceiptTab.summary.vat')}
              </Typography>
              <Chip 
                label={receiptSettings.showVat ? t('kioskReceiptTab.summary.show') : t('kioskReceiptTab.summary.hide')}
                color={receiptSettings.showVat ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Grid>

          {/* Teşekkür Mesajı */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('kioskReceiptTab.summary.thankYouMessage')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {receiptSettings.thankYouMessage || '-'}
              </Typography>
            </Box>
          </Grid>

          {/* Standart Alt Not */}
          {receiptSettings.standardFooterNote && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('kioskReceiptTab.summary.standardFooterNote')}
                </Typography>
                <Typography variant="body2">
                  {receiptSettings.standardFooterNote}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Adisyon Ayarları Modalı */}
      <KioskReceiptSettingsModal
        open={receiptSettingsModalOpen}
        onClose={handleCloseReceiptSettingsModal}
        onSave={handleSaveReceiptSettings}
        initialSettings={receiptSettings}
        onImageUpload={onImageUpload}
      />
    </Box>
  );
};

export default KioskReceiptSettingsTab;

